import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useAuth } from "../../context/AuthContext";
import {
  FiUser,
  FiLogOut,
  FiChevronDown,
  FiDatabase,
  FiBarChart2,
  FiPlay,
  FiClock,
  FiLock,
} from "react-icons/fi";

const Header = ({ onAuthClick }) => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error("Sign out error:", error);
    } finally {
      setShowUserMenu(false);
      // Always navigate to home even if signOut fails
      navigate("/");
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const getUserDisplayName = () => {
    // Check for full name from user metadata (Google OAuth)
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name;
    }
    // Check for name field
    if (user?.user_metadata?.name) {
      return user.user_metadata.name;
    }
    // Check identities data
    if (user?.identities?.[0]?.identity_data?.full_name) {
      return user.identities[0].identity_data.full_name;
    }
    if (user?.identities?.[0]?.identity_data?.name) {
      return user.identities[0].identity_data.name;
    }
    // Fallback to email
    if (user?.email) {
      return user.email;
    }
    return "User";
  };

  const getUserAvatar = () => {
    // Check for avatar URL from user metadata (Google OAuth)
    if (user?.user_metadata?.avatar_url) {
      return user.user_metadata.avatar_url;
    }
    // Check for picture field
    if (user?.user_metadata?.picture) {
      return user.user_metadata.picture;
    }
    // Check identities data
    if (user?.identities?.[0]?.identity_data?.avatar_url) {
      return user.identities[0].identity_data.avatar_url;
    }
    if (user?.identities?.[0]?.identity_data?.picture) {
      return user.identities[0].identity_data.picture;
    }
    return null;
  };

  return (
    <header className="border-b border-gray-100 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-[70px] items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-3 transition-transform duration-200 hover:-translate-y-0.5"
          >
            <img
              src="/inceptbench_logo.svg"
              alt="InceptBench"
              className="h-10"
            />
          </Link>

          {/* Navigation */}
          <nav className="flex items-center gap-1 rounded-full bg-gray-100/80 p-1">
            {/* Benchmark Sets - visible to all */}
            <Link
              to="/benchmark-sets"
              className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
                isActive("/benchmark-sets")
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:bg-white/50 hover:text-gray-900"
              }`}
            >
              <FiDatabase className="size-4" />
              <span>Datasets</span>
            </Link>

            {/* Dashboard */}
            {user ? (
              <Link
                to="/dashboard"
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
                  isActive("/dashboard")
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:bg-white/50 hover:text-gray-900"
                }`}
              >
                <FiBarChart2 className="size-4" />
                <span>Dashboard</span>
              </Link>
            ) : (
              <button
                onClick={() => onAuthClick("signin", "/dashboard")}
                className="group relative flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-gray-400 transition-all duration-200 hover:text-gray-500"
                title="Sign in to access"
              >
                <FiBarChart2 className="size-4" />
                <span>Dashboard</span>
                <FiLock className="size-3 opacity-60" />
                <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                  Sign in to access
                </span>
              </button>
            )}

            {/* Run */}
            {user ? (
              <Link
                to="/run"
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
                  isActive("/run")
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:bg-white/50 hover:text-gray-900"
                }`}
              >
                <FiPlay className="size-4" />
                <span>Run</span>
              </Link>
            ) : (
              <button
                onClick={() => onAuthClick("signin", "/run")}
                className="group relative flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-gray-400 transition-all duration-200 hover:text-gray-500"
                title="Sign in to access"
              >
                <FiPlay className="size-4" />
                <span>Run</span>
                <FiLock className="size-3 opacity-60" />
                <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                  Sign in to access
                </span>
              </button>
            )}

            {/* Past Runs */}
            {user ? (
              <Link
                to="/past-runs"
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
                  isActive("/past-runs")
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:bg-white/50 hover:text-gray-900"
                }`}
              >
                <FiClock className="size-4" />
                <span>Past Runs</span>
              </Link>
            ) : (
              <button
                onClick={() => onAuthClick("signin", "/past-runs")}
                className="group relative flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-gray-400 transition-all duration-200 hover:text-gray-500"
                title="Sign in to access"
              >
                <FiClock className="size-4" />
                <span>Past Runs</span>
                <FiLock className="size-3 opacity-60" />
                <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                  Sign in to access
                </span>
              </button>
            )}
          </nav>

          {/* Auth Buttons / User Menu */}
          <div className="flex items-center space-x-4">
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:bg-gray-100"
                >
                  {getUserAvatar() ? (
                    <img
                      src={getUserAvatar()}
                      alt="User avatar"
                      className="size-6 rounded-full border-2 border-gray-200 object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <FiUser className="size-4" />
                  )}
                  <span className="max-w-[150px] truncate">
                    {getUserDisplayName()}
                  </span>
                  <FiChevronDown
                    className={`size-3.5 transition-transform duration-200 ${
                      showUserMenu ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {showUserMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowUserMenu(false)}
                      onKeyDown={(e) =>
                        e.key === "Escape" && setShowUserMenu(false)
                      }
                      role="button"
                      tabIndex={0}
                      aria-label="Close menu"
                    />
                    <div className="absolute right-0 z-20 mt-2 min-w-[240px] rounded-xl border border-gray-200 bg-white shadow-large">
                      {/* User Info Section */}
                      <div className="flex items-center gap-3 border-b border-gray-100 p-3">
                        {getUserAvatar() ? (
                          <img
                            src={getUserAvatar()}
                            alt="User avatar"
                            className="size-10 shrink-0 rounded-full border-2 border-gray-200 object-cover"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <div className="flex size-10 shrink-0 items-center justify-center rounded-full border-2 border-gray-200 bg-gradient-to-br from-primary-50 to-primary-100 text-primary-600">
                            <FiUser className="size-5" />
                          </div>
                        )}
                        <div className="flex-1 overflow-hidden">
                          <div className="truncate text-sm font-semibold text-gray-900">
                            {getUserDisplayName()}
                          </div>
                          <div className="truncate text-xs text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>

                      {/* Sign Out Button */}
                      <button
                        onClick={handleSignOut}
                        className="flex w-full items-center space-x-2 px-4 py-3 text-left text-sm text-gray-700 transition-all duration-200 hover:bg-gray-50 hover:text-red-600"
                      >
                        <FiLogOut className="size-4" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => onAuthClick("signin")}
                  className="rounded-lg border border-gray-200 bg-transparent px-5 py-2.5 text-sm font-medium text-gray-600 transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                >
                  Sign In
                </button>
                <button
                  onClick={() => onAuthClick("signup")}
                  className="rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-medium text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                >
                  Sign Up
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

Header.propTypes = {
  onAuthClick: PropTypes.func.isRequired,
};

export default Header;
